import React from "react";
import { getJwtClaims, IWithAuthProps } from "shared-auth";
import { captureExceptionEvent } from "shared-services";
import {
  HALManager,
  HALResources,
  SettingManager,
  UserClaimTypes,
  UserManager,
  UserRoleManager,
  UserRoles,
} from "core";

import { matchPath } from "react-router-dom";
import { parseQuery, parseToUrlQuery } from "shared/utils/http";

import { ICancelablePromise, makeCancelable } from "shared/utils/promise";

import { Profile, Settings, WhoAmI } from "models";
import ProfileService from "services/ProfileService";
import { RouterProps } from "component-library";
import { NOT_FOUND_FULL_PATH } from "app/config/routes";

const VIEW_PIN_PATTERN = /\d{4}-\d{4}-\d{4}\/?$/;

const SPOTLIGHT_MEMBER_GROUPS_CLAIM = "spotlight.MemberGroups";

interface IAuthenticationState {
  isLoaded: boolean;
}

export declare type WithUserInfoProps = RouterProps & IWithAuthProps;

export default function withUserInfo<P extends WithUserInfoProps>(
  WrappedComponent:
    | React.ComponentClass<P>
    | React.FC<React.PropsWithChildren<P>>
): typeof React.Component {
  return class extends React.Component<P, IAuthenticationState> {
    public readonly state: Readonly<IAuthenticationState> = { isLoaded: false };
    private whoAmIPromise: ICancelablePromise<WhoAmI>;
    private profilePromise: ICancelablePromise<Profile>;
    private settingPromise: ICancelablePromise<Settings>;

    public render() {
      const { isLoaded } = this.state;

      return isLoaded ? <WrappedComponent {...this.props} /> : null;
    }

    public async componentDidMount() {
      const { auth } = this.props;

      if (!auth.isAuthenticated) {
        this.setState({ isLoaded: true });

        return;
      }

      try {
        await this.initUserData();

        this.setState({ isLoaded: true });
      } catch (error) {
        captureExceptionEvent(error);
      }
    }

    public componentWillUnmount() {
      this.whoAmIPromise?.cancel();
      this.profilePromise?.cancel();
      this.settingPromise?.cancel();
    }

    private initUserData = async () => {
      const viewPinMatch = window.location.pathname.match(VIEW_PIN_PATTERN);
      const isCasting = UserRoleManager.isInRole(UserRoles.castingDirector);

      this.extractClaimsFromToken();

      if (!UserManager.userId) {
        try {
          await this.loadUser();
        } catch (error) {
          // if on profilenotfound page then ignore loading user failing
          const currentRoute = this.props.router.location.pathname;
          const matchProfileNotFound = matchPath(
            { path: NOT_FOUND_FULL_PATH },
            currentRoute
          );

          if (!matchProfileNotFound) {
            throw error;
          }
        }
      }

      if (isCasting) {
        this.setAdditionalCastingClaims();
      } else if (!viewPinMatch) {
        await this.loadProfile();
      }
    };

    private loadProfile = async () => {
      this.settingPromise = makeCancelable(ProfileService.getSettings());
      this.profilePromise = makeCancelable(ProfileService.getProfile());

      try {
        const setting = await this.settingPromise.promise;

        SettingManager.settings = setting;
      } catch (error) {
        captureExceptionEvent(error);
      }

      try {
        const profile = await this.profilePromise.promise;
        this.updateUserProfileData(profile);
      } catch (error) {
        captureExceptionEvent(error);
      }
    };

    private loadUser = async () => {
      this.whoAmIPromise = makeCancelable(ProfileService.whoAmI());

      try {
        const user = await this.whoAmIPromise.promise;

        this.setUser(user);
      } catch (error) {
        captureExceptionEvent(error);
      }
    };

    private setUser = (user: WhoAmI) => {
      const { type, id } = user;

      UserManager.setClaim(UserClaimTypes.role, type);

      if (id) {
        UserManager.setClaim(UserClaimTypes.userId, id);
      }

      HALManager.setHalFor(
        HALResources.getProfile,
        HALManager.getProfileByProfileIdUrl(UserManager.userId)
      );
    };

    private updateUserProfileData = (profile: Profile) => {
      const { forename, surname, viewPin, artistType } = profile;

      UserManager.setClaim(
        UserClaimTypes.profileFullName,
        `${forename} ${surname}`
      );
      UserManager.setClaim(UserClaimTypes.viewPin, viewPin);

      if (artistType) {
        UserManager.setClaim(UserClaimTypes.artistType, artistType.toString());
      }

      HALManager.setHalFor(
        HALResources.getGallery,
        HALManager.getGalleryByProfileIdUrl(UserManager.userId)
      );
      HALManager.setHalFor(
        HALResources.patchGallery,
        HALManager.getGalleryByProfileIdUrl(UserManager.userId)
      );
      HALManager.setHalFor(
        HALResources.getMainPhoto,
        HALManager.getMainPhotoByProfileIdUrl(UserManager.userId)
      );
    };

    private setAdditionalCastingClaims = () => {
      const {
        router: {
          location: { search, pathname },
          navigate,
        },
      } = this.props;

      const { disabilities, gender, ...queryObject } = parseQuery(search);

      if (disabilities) {
        UserManager.setClaim(UserClaimTypes.isDisabilitiesVisible, "true");
      }

      if (gender) {
        UserManager.setClaim(UserClaimTypes.isGenderVisible, "true");
      }

      navigate(pathname + parseToUrlQuery(queryObject), { replace: true });
    };

    private extractClaimsFromToken = () => {
      const claims = getJwtClaims();

      if (claims) {
        const userGroupClaim = claims[SPOTLIGHT_MEMBER_GROUPS_CLAIM];

        UserManager.setClaim(UserClaimTypes.role, userGroupClaim as string);

        if (claims.profile_id) {
          UserManager.setClaim(
            UserClaimTypes.userId,
            claims.profile_id as string
          );
        }

        HALManager.setHalFor(
          HALResources.getProfile,
          HALManager.getProfileByProfileIdUrl(UserManager.userId)
        );
      }
    };
  };
}
